import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Save as SaveIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { settingsAPI } from '../services/api';

const emptySettings = {
  clinicName: '',
  tagline: '',
  address: { line1: '', line2: '', city: '', state: '', pincode: '' },
  phone: '',
  alternatePhone: '',
  email: '',
  website: '',
  gstin: '',
  panNumber: '',
  registrationNumber: '',
  clinicTimings: '',
  receiptFooterNote: '',
  defaultGstRate: 0,
  isCompositionScheme: false,
  whatsappEnabled: false,
  smsEnabled: false,
};

const ClinicSettings = () => {
  const [settings, setSettings] = useState(emptySettings);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    settingsAPI.getSettings().then(res => setSettings({ ...emptySettings, ...res.data.data, address: { ...emptySettings.address, ...res.data.data.address } }))
      .catch(() => setSnackbar({ open: true, message: 'Failed to load settings', severity: 'error' }));
  }, []);

  const handleSave = async () => {
    try {
      await settingsAPI.updateSettings(settings);
      setSnackbar({ open: true, message: 'Clinic settings updated successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save settings', severity: 'error' });
    }
  };

  const set = (field, value) => setSettings({ ...settings, [field]: value });
  const setAddr = (field, value) => setSettings({ ...settings, address: { ...settings.address, [field]: value } });

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
          <SettingsIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="700">Clinic Settings</Typography>
          <Typography variant="body2" color="text.secondary">Letterhead, GST, and configuration used across invoices &amp; prescriptions</Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>Save Changes</Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Clinic Identity</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Clinic Name" value={settings.clinicName} onChange={(e) => set('clinicName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Tagline" value={settings.tagline} onChange={(e) => set('tagline', e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Address</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Address Line 1" value={settings.address.line1} onChange={(e) => setAddr('line1', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Address Line 2" value={settings.address.line2} onChange={(e) => setAddr('line2', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={settings.address.city} onChange={(e) => setAddr('city', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="State" value={settings.address.state} onChange={(e) => setAddr('state', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Pincode" value={settings.address.pincode} onChange={(e) => setAddr('pincode', e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Contact</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Phone" value={settings.phone} onChange={(e) => set('phone', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Alternate Phone" value={settings.alternatePhone} onChange={(e) => set('alternatePhone', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Email" value={settings.email} onChange={(e) => set('email', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Website" value={settings.website} onChange={(e) => set('website', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Clinic Timings" value={settings.clinicTimings} onChange={(e) => set('clinicTimings', e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Statutory &amp; GST</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="GSTIN" value={settings.gstin} onChange={(e) => set('gstin', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="PAN Number" value={settings.panNumber} onChange={(e) => set('panNumber', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Clinic Registration No." value={settings.registrationNumber} onChange={(e) => set('registrationNumber', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Default GST Rate (%)" value={settings.defaultGstRate} onChange={(e) => set('defaultGstRate', parseFloat(e.target.value) || 0)} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormControlLabel
              control={<Switch checked={settings.isCompositionScheme} onChange={(e) => set('isCompositionScheme', e.target.checked)} />}
              label="Registered under GST Composition Scheme (no GST charged on invoices)"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Communication</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={settings.whatsappEnabled} onChange={(e) => set('whatsappEnabled', e.target.checked)} />}
              label="Enable WhatsApp reminders (click-to-chat)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={settings.smsEnabled} onChange={(e) => set('smsEnabled', e.target.checked)} />}
              label="Enable SMS reminders (requires gateway integration)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Receipt / Invoice Footer Note" value={settings.receiptFooterNote} onChange={(e) => set('receiptFooterNote', e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ClinicSettings;
